/**
 * AP-13 — Gegenstück zu `src/license/LicensePublicKey.h` + `VerifyLicenseSignature()`.
 *
 * Derselbe RSA-2048-Schlüssel wie im C++-Client, nur als Hex-Zeichenkette statt
 * als `constexpr unsigned char[]`. Verifiziert wird mit Web Crypto
 * (`RSASSA-PKCS1-v1_5`/SHA-256) statt mit bcrypt — Signaturformat und
 * signierte Nutzlast (`key|hardware_id|expiry`) bleiben identisch, ein
 * Aktivierungstoken des bestehenden `license_server/` gilt also für beide
 * Clients.
 */

/** Byte-für-Byte `kLicenseRsaModulus` aus LicensePublicKey.h (256 Byte). */
export const LICENSE_RSA_MODULUS_HEX =
  '89dec42492da750393abb46732b01ee6d3896dc3079ff5ae18bf972cc2aafdff' +
  'd6dde1d0abd9c56cf854c69c0fdc79824adad82529effadc1d4160f28d3a9763' +
  'e0a32c1848101b2fd05bb3f026d94a52ae4662dba4b30a039d1c3188291e488b' +
  '02184ee3c66c2a0d33c285dede657ec3819044f983e6310ab00aa9edd7b89a15' +
  '72585d0196ed24dd328d3375d5221e9a535d2c93b19ca2c43bf75692d7648209' +
  'fbcd932cc896dd6dc139034c588fdba4ed049ff1b56c219b104f0b778f7f5234' +
  'f3c9aace4f266a4cc53b053163d9079b5497053ae3e48ad7868c5f811c08250f' +
  'ab27318355f446f89c1a9654643026de00e8eb24b933c8981d36c67b9a9d0a51';

/** `kLicenseRsaPublicExponent` = 65537. */
export const LICENSE_RSA_EXPONENT_HEX = '010001';

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('hex string must have an even length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new Error('invalid hex string');
    out[i] = byte;
  }
  return out;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** JWK statt SPKI: spart das DER-Gerüst, der Modulus liegt schon roh vor. */
export async function importLicensePublicKey(
  modulusHex: string = LICENSE_RSA_MODULUS_HEX,
  exponentHex: string = LICENSE_RSA_EXPONENT_HEX,
): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    {
      kty: 'RSA',
      n: bytesToBase64Url(hexToBytes(modulusHex)),
      e: bytesToBase64Url(hexToBytes(exponentHex)),
      alg: 'RS256',
      ext: true,
    },
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['verify'],
  );
}

/**
 * Prüft die Serversignatur über `key|hardware_id|expiry`. Ein defekter
 * Hex-String ist kein Fehlerfall, sondern schlicht „ungültig" — genau wie
 * `HexDecode()` im Original, das dann `false` zurückgibt.
 */
export async function verifyLicenseSignature(
  publicKey: CryptoKey,
  payload: string,
  signatureHex: string,
): Promise<boolean> {
  if (signatureHex === '' || signatureHex.length % 2 !== 0) return false;
  let signature: Uint8Array;
  try {
    signature = hexToBytes(signatureHex);
  } catch {
    return false;
  }
  try {
    return await crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      publicKey,
      signature as unknown as BufferSource,
      new TextEncoder().encode(payload) as unknown as BufferSource,
    );
  } catch {
    return false;
  }
}
